import { IconTrash } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from '../../atoms/alert-dialog'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { FC, useCallback, useState } from 'react'
import { Tenant } from '../../../types/tenant'
import { Input } from '../../atoms/inputs/Input'
import { api } from 'src/api/root'
import { enqueueSnackbar } from 'notistack'
import TooltipWrapper from '../../atoms/TooltipWrapper'
import Typography from '../../atoms/Typography'
import Loader from '../../atoms/Loader'
import { formatCount } from '../../../enviroment/utils/formatCount'
import ExpandableList from '../../molecules/ExpandableList'

interface DeleteWorkspaceDialogProps {
  workspace: Tenant
  isCurrentTenant?: boolean
}

const DeleteWorkspaceDialog: FC<DeleteWorkspaceDialogProps> = ({ workspace, isCurrentTenant = false }) => {
  const [opened, setOpened] = useState(false)
  const [step, setStep] = useState<'project-warning' | 'confirm'>('confirm')
  const [confirmName, setConfirmName] = useState('')

  const { data: exclusiveProjects, isLoading: isProjectsLoading } =
    api.superAdmin.getExclusiveProjectsOfTenant.useQuery(workspace.id, {
      enabled: opened,
    })

  const liveCount = exclusiveProjects?.length ?? 0
  const effectiveStep: 'project-warning' | 'confirm' = step === 'project-warning' && liveCount === 0 ? 'confirm' : step

  const handleOpen = useCallback((open: boolean) => {
    setOpened(open)
    if (open) {
      setStep('project-warning')
      setConfirmName('')
    }
  }, [])

  const handleClose = useCallback(() => {
    setOpened(false)
    setConfirmName('')
  }, [])

  const { mutateAsync, isPending } = api.superAdmin.deleteTenant.useMutation()

  const handleDelete = useCallback(async () => {
    try {
      const { deletedProjects, failedProjects } = await mutateAsync(workspace.id)
      const message =
        failedProjects > 0
          ? `Workspace "${workspace.name}" deleted. ${deletedProjects} project(s) deleted, ${failedProjects} failed.`
          : `Workspace "${workspace.name}" deleted successfully.`
      enqueueSnackbar(message, { variant: failedProjects > 0 ? 'warning' : 'success' })
      handleClose()
    } catch (error) {
      console.log(error)
    }
  }, [mutateAsync, handleClose, workspace.id, workspace.name])

  const isDeleteDisabled = confirmName !== workspace.name || isPending

  return (
    <AlertDialog open={opened} onOpenChange={handleOpen}>
      {isCurrentTenant ? (
        <TooltipWrapper content="Cannot delete the current workspace" side="top">
          <AlertDialogTrigger
            disabled
            className="ml-2 flex h-10 w-16 items-center justify-center rounded-full border border-red-ramo text-red-600 transition-all hover:cursor-pointer hover:bg-red-600 hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:border-gray-600 disabled:text-gray-600 disabled:hover:bg-transparent disabled:hover:text-gray-600"
          >
            <IconTrash size={24} />
          </AlertDialogTrigger>
        </TooltipWrapper>
      ) : (
        <AlertDialogTrigger className="ml-2 flex h-10 w-16 items-center justify-center rounded-full border border-red-ramo text-red-600 transition-all hover:cursor-pointer hover:bg-red-600 hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:border-gray-600 disabled:text-gray-600 disabled:hover:bg-transparent disabled:hover:text-gray-600">
          <IconTrash size={24} />
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFADAD]">
            <IconTrash className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex w-full flex-col justify-center gap-4">
            <span className="text-center text-xl text-green-50">Delete Workspace</span>

            {isProjectsLoading ? (
              <div className="flex justify-center py-4">
                <Loader className="text-gray-400" />
              </div>
            ) : effectiveStep === 'project-warning' ? (
              <>
                <Typography variant="base" className="text-center text-gray-50">
                  This workspace has{' '}
                  <Typography variant="base-semibold" className="inline">
                    {formatCount(liveCount, 'exclusive project')}
                  </Typography>{' '}
                  that will be permanently deleted. Are you sure you want to continue?
                </Typography>

                <ExpandableList
                  items={exclusiveProjects?.map((project) => ({
                    id: project.id,
                    title: project.name,
                    subtitle: project.id,
                  }))}
                />
              </>
            ) : (
              <>
                <Typography variant="base" className="text-center text-gray-50">
                  Please type{' '}
                  <Typography variant="base-semibold" className="inline">
                    {workspace.name}
                  </Typography>{' '}
                  to confirm deletion.
                </Typography>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder="Enter workspace name"
                  autoComplete="off"
                  className="text-center"
                />
              </>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={handleClose} className="w-40">
            Cancel
          </AlertDialogCancel>
          {!isProjectsLoading &&
            (effectiveStep === 'project-warning' ? (
              <PrimaryButton
                onClick={() => setStep('confirm')}
                className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
              >
                Continue
              </PrimaryButton>
            ) : (
              <PrimaryButton
                disabled={isDeleteDisabled}
                isLoading={isPending}
                onClick={handleDelete}
                className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
              >
                Delete
              </PrimaryButton>
            ))}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteWorkspaceDialog
